import { useMemo } from 'react';

import { EWebtraderResetPasswordType } from '@aqx-core/common/api/config';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

export const useAuthRoutes = () => {
  const companyProfile = useCompanyProfile();
  // TODO: Handle registration URL for android/ios/web accordingly once doing 3.0 for mobile
  const {
    companyRegistrationUrl,
    mt5DemoRegistrationUrl,
    mt4LiveRegistrationUrl,
    crmForgotPasswordType,
    webtraderResetPasswordType,
    mt4DemoRegistrationUrl,
    mt5LiveRegistrationUrl
  } = companyProfile || {};

  const {
    hasCompanyLoginConfig,
    hasDemoAccountCreation,
    hasMT4DemoLoginConfig,
    hasMT5DemoLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT5LiveLoginConfig,
    hasResetPasswordAccess,
    hasForgotPasswordAccess,
    hasExperienceAccess
  } = useMemo(() => {
    return validateCompanyAccess(companyProfile);
  }, [companyProfile]);

  // ** LIVE **
  // There is currently no live sign up route, there is only redirection to the url specified in root admin
  const hasLiveSignUpButton = useMemo(() => {
    if (hasMT4LiveLoginConfig && mt4LiveRegistrationUrl) {
      return true;
    }
    if (hasMT5LiveLoginConfig && mt5LiveRegistrationUrl) {
      return true;
    }
    return false;
  }, [hasMT4LiveLoginConfig, hasMT5LiveLoginConfig, mt4LiveRegistrationUrl, mt5LiveRegistrationUrl]);

  const hasLiveForgotPasswordButton = useMemo(
    () => (hasMT4LiveLoginConfig || hasMT5LiveLoginConfig) && hasForgotPasswordAccess,
    [hasForgotPasswordAccess, hasMT4LiveLoginConfig, hasMT5LiveLoginConfig]
  );

  const hasLiveForgotPasswordRoute = useMemo(
    () => hasLiveForgotPasswordButton && webtraderResetPasswordType === EWebtraderResetPasswordType.INTEGRATION,
    [hasLiveForgotPasswordButton, webtraderResetPasswordType]
  );

  // ** DEMO **
  // For demo, there is only sign up for MT4 or MT5 demo.
  // There is no sign up UI/redirection for experience demo.
  // Experience demo sign up is handled from AQX website
  // Priority: hasExperienceAccess > hasDemoAccountCreation > mt4DemoRegistrationUrl / mt5DemoRegistrationUrl
  // 1. hasExperienceAccess - First flag to show/hide the button (handled externally on AQX website)
  // 2. hasDemoAccountCreation - BO config "Show Demo Account Creation" to show internal form
  // 3. mt4DemoRegistrationUrl / mt5DemoRegistrationUrl - RA config demo URL to show external link
  const { hasButton: hasDemoSignUpButton, hasRoute: hasDemoSignUpRoute } = useMemo(() => {
    if (hasExperienceAccess) {
      return { hasButton: false, hasRoute: false };
    }

    if (hasDemoAccountCreation) {
      return { hasButton: true, hasRoute: true };
    }

    if (mt4DemoRegistrationUrl || mt5DemoRegistrationUrl) {
      return { hasButton: true, hasRoute: false };
    }

    return { hasButton: false, hasRoute: false };
  }, [hasDemoAccountCreation, hasExperienceAccess, mt4DemoRegistrationUrl, mt5DemoRegistrationUrl]);

  // For demo, only experience demo has reset password
  // If have demo reset password, have both route and button for it
  const hasDemoResetPassword = useMemo(
    () => (hasMT4DemoLoginConfig || hasMT5DemoLoginConfig) && hasExperienceAccess,
    [hasExperienceAccess, hasMT4DemoLoginConfig, hasMT5DemoLoginConfig]
  );

  // ** CRM **
  const hasCrmSignUpButton = useMemo(
    () => !!companyRegistrationUrl && hasCompanyLoginConfig,
    [companyRegistrationUrl, hasCompanyLoginConfig]
  );

  const hasCrmSignUpRoute = useMemo(
    () => companyRegistrationUrl?.toLocaleLowerCase() === 'form' && hasCompanyLoginConfig,
    [companyRegistrationUrl, hasCompanyLoginConfig]
  );

  const hasCrmForgotPasswordButton = useMemo(
    () => crmForgotPasswordType && hasResetPasswordAccess && hasCompanyLoginConfig,
    [crmForgotPasswordType, hasCompanyLoginConfig, hasResetPasswordAccess]
  );

  const hasCrmForgotPasswordRoute = useMemo(
    () => hasCrmForgotPasswordButton && crmForgotPasswordType === EWebtraderResetPasswordType.INTEGRATION,
    [crmForgotPasswordType, hasCrmForgotPasswordButton]
  );

  return {
    hasLiveSignUpButton,
    hasLiveForgotPasswordButton,
    hasLiveForgotPasswordRoute,
    hasDemoSignUpButton,
    hasDemoSignUpRoute,
    hasDemoResetPassword,
    hasCrmSignUpButton,
    hasCrmSignUpRoute,
    hasCrmForgotPasswordButton,
    hasCrmForgotPasswordRoute,
    companyProfile
  };
};
